/** Red varies fastest, then green, then blue, as required by CUBE tables. */
export class CompiledCubeLUT {
    size;
    table;
    constructor(cube) {
        if (cube.length > 2 * 1024 * 1024)
            throw new Error('Malformed CUBE table: exceeds 2 MiB.');
        let size = 0;
        const headers = new Set();
        const values = [];
        for (const raw of cube.split(/\r?\n/)) {
            const line = raw.replace(/#.*/, '').trim();
            if (!line || line.startsWith('TITLE '))
                continue;
            if (line.startsWith('LUT_3D_SIZE ')) {
                const fields = line.split(/\s+/);
                if (headers.has('LUT_3D_SIZE') || fields.length !== 2)
                    throw new Error('Malformed CUBE dimensions header.');
                headers.add('LUT_3D_SIZE');
                size = Number(fields[1]);
                if (size !== 17 && size !== 33)
                    throw new Error('Malformed CUBE dimensions: only 17 or 33 are supported.');
                continue;
            }
            if (line.startsWith('DOMAIN_')) {
                const [name, ...coordinates] = line.split(/\s+/);
                const expected = name === 'DOMAIN_MIN' ? 0 : 1;
                if (headers.has(name) || !['DOMAIN_MIN', 'DOMAIN_MAX'].includes(name) || coordinates.length !== 3 || coordinates.some((value) => Number(value) !== expected)) {
                    throw new Error('PCE audit requires a normalized 0..1 CUBE domain.');
                }
                headers.add(name);
                continue;
            }
            const row = line.split(/\s+/).map(Number);
            if (row.length !== 3 || row.some((value) => !Number.isFinite(value) || value < 0 || value > 1)) {
                throw new Error('Malformed CUBE table: expected three finite values in 0..1.');
            }
            values.push(...row);
            if (!size || values.length > size ** 3 * 3)
                throw new Error('Malformed CUBE dimensions or excess rows.');
        }
        if ((size !== 17 && size !== 33) || values.length !== size ** 3 * 3) {
            throw new Error('Malformed CUBE table: dimensions and row count do not agree.');
        }
        this.size = size;
        this.table = Float32Array.from(values);
    }
    rgb(pixel) {
        const maximum = this.size - 1;
        const positions = pixel.map((value) => Math.max(0, Math.min(1, value)) * maximum);
        const lower = positions.map(Math.floor);
        const upper = lower.map((value) => Math.min(maximum, value + 1));
        const weights = positions.map((value, i) => value - lower[i]);
        const output = [0, 0, 0];
        for (let b = 0; b < 2; b++)
            for (let g = 0; g < 2; g++)
                for (let r = 0; r < 2; r++) {
                    const index = (((b ? upper[2] : lower[2]) * this.size + (g ? upper[1] : lower[1])) * this.size + (r ? upper[0] : lower[0])) * 3;
                    const weight = (r ? weights[0] : 1 - weights[0]) * (g ? weights[1] : 1 - weights[1]) * (b ? weights[2] : 1 - weights[2]);
                    output[0] += this.table[index] * weight;
                    output[1] += this.table[index + 1] * weight;
                    output[2] += this.table[index + 2] * weight;
                }
        return output;
    }
}
export function createCubeLUT(transform, size = 17, title = 'pre match') {
    if (size !== 17 && size !== 33)
        throw new Error('PCE CUBE exports support only sizes 17 or 33.');
    const lines = [`TITLE "${title.replaceAll('"', "'").replaceAll(/[\r\n]/g, ' ')}"`, `LUT_3D_SIZE ${size}`, 'DOMAIN_MIN 0 0 0', 'DOMAIN_MAX 1 1 1'];
    for (let b = 0; b < size; b++)
        for (let g = 0; g < size; g++)
            for (let r = 0; r < size; r++) {
                const rgb = transform([r / (size - 1), g / (size - 1), b / (size - 1)]);
                if (rgb.some((value) => !Number.isFinite(value)))
                    throw new Error('Cannot export a non-finite LUT correction.');
                lines.push(rgb.map((value) => Math.max(0, Math.min(1, value)).toFixed(6)).join(' '));
            }
    return `${lines.join('\n')}\n`;
}
export function applyCubeLUTToFrame(frame, lut) {
    const data = new Uint8ClampedArray(frame.data);
    for (let i = 0; i < data.length; i += 4) {
        if (!data[i + 3])
            continue;
        const rgb = lut.rgb([data[i] / 255, data[i + 1] / 255, data[i + 2] / 255]);
        data[i] = Math.round(rgb[0] * 255);
        data[i + 1] = Math.round(rgb[1] * 255);
        data[i + 2] = Math.round(rgb[2] * 255);
    }
    return typeof ImageData !== 'undefined'
        ? new ImageData(data, frame.width, frame.height, { colorSpace: frame.colorSpace })
        : { data, width: frame.width, height: frame.height, colorSpace: frame.colorSpace };
}
